import { useMemo } from 'react'
import { DataGridPremium } from '@mui/x-data-grid-premium'
import { ItemHistoryEntry } from './types'
import { deriveMovementRows } from './deriveMovementRow'
import columns from './columns'

export type MovementHistoryGridProps = {
	entries: ItemHistoryEntry[],
	loading: boolean,
}

/**
 * The Transaction History table. Renders derived `MovementRow`s in a compact DataGrid
 * Premium (the migration-plan standard for embedded/detail tables). Client-side
 * pagination is fine for the mock; when the real cursor-paginated query lands, switch
 * to server mode (`paginationMode="server"` + `rowCount` + `onPaginationModelChange`).
 */
export default function MovementHistoryGrid( { entries, loading }: MovementHistoryGridProps ) {
	const rows = useMemo( () => deriveMovementRows( entries ), [ entries ] )

	return (
		<DataGridPremium
			rows={rows}
			columns={columns}
			getRowId={( row ) => row.id}
			loading={loading}
			density="compact"
			getRowHeight={() => 'auto'}
			disableRowSelectionOnClick
			disableColumnMenu
			disableColumnFilter
			hideFooterSelectedRowCount
			pagination
			pageSizeOptions={[ 10, 25, 50 ]}
			initialState={{
				pagination: { paginationModel: { pageSize: 25 } },
				sorting: { sortModel: [ { field: 'date', sort: 'desc' } ] },
			}}
			sx={{
				// Bounded, viewport-adaptive height so the table scrolls its rows internally instead of
				// growing the page. The offset is the measured chrome above the grid (~430px, mostly the
				// state panel) plus ~40px margin, so the table's bottom stays that far above the fold on
				// every window height and it grows on taller screens. The low floor keeps short desktop
				// windows (~1366×768-class) within the fold too. Horizontal scroll only appears when the
				// columns can't fit (minWidths).
				'height': 'calc(100vh - 470px)',
				'minHeight': 200,
				'bgcolor': 'background.paper',
				'& .MuiDataGrid-cell': { alignItems: 'flex-start', py: 0.75 },
			}}
		/>
	)
}
